import { DatePicker, Descriptions, Select } from "antd";
import moment from "moment";
import { FC, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from 'react-i18next';
import { createSemesterTest, getSemesterTest, updateSemesterTest } from "../../../api/semesterTest.api";
import useAxios from "../../../hooks/useAxios";
import useNotification from "../../../hooks/useNotification";
import { IGetSemesterTest } from "../../../models/semesterTest.model";
import { SemesterContents } from "../../../pages/program/semester";
import { RootState } from "../../../store/rootReducer";
import Button from "../../Button";
import ErrorResponse from "../../ErrorResponse";
import Loader from "../../Loader";
import Radio from "../../Radio";
import { getReverseYears } from "../../../lib/util";
import SelectEduStep from "../../SelectEduStep";
import { SEMESTER_TYPE } from "../../../constans";

interface IProps {
  semesterCode: string;
  setTab: (tab: SemesterContents) => void;
  setGetTestsTrigger: any;
  scheduleCode: number|undefined;
  selSemesterType?: string;
}

const TestEdit: FC<IProps> = ({
  semesterCode,
  setTab,
  setGetTestsTrigger,
  scheduleCode,
  selSemesterType,
}) => {
  const { t } = useTranslation();
  const { commonCodes } = useSelector((state: RootState) => state.commonCode);
  // 연도
  const SY0005 = commonCodes.find(item => item.commonCode === 'SY0005')?.detailCode;
  // 시험 회차
  const SY0011 = commonCodes.find(item => item.commonCode === 'SY0011')?.detailCode;
  const { infoNotification, successNotification } = useNotification();
  const { data: res, isLoading, error, execute } = useAxios<IGetSemesterTest>();
  const { data: submitRes, isLoading: submitIsLoading, error: submitError, execute: submitExecute } = useAxios();

  const [name, setName] = useState('');
  const [eduStepCode, setEduStepCode] = useState<string>();
  const [examDiv, setExamDiv] = useState<string>();
  const [checkYn, setCheckYn] = useState('N');
  const [srtDate, setSrtDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [yearCode, setYearCode] = useState<string>();
  const [testSeq, setTestSeq] = useState<string>();
  const [cacExamDate, setCacExamDate] = useState<string>();

  useEffect(() => {
    if (scheduleCode) {
      execute(getSemesterTest(scheduleCode));
    }
  }, [scheduleCode]);

  useEffect(() => {
    if (res) {
      setEduStepCode(res?.data?.eduStepCode);
      setExamDiv(res?.data?.examDiv);
      setName(res?.data?.name);
      setSrtDate(res?.data?.srtDate);
      setEndDate(res?.data?.endDate);
      setYearCode(res?.data?.yearCode);
      setTestSeq(res?.data?.testSeq);
      setCheckYn(res?.data?.checkYn);
      setCacExamDate(res?.data?.cacExamDate);
    }
  }, [res]);

  useEffect(() => {
    if (submitRes) {
      setGetTestsTrigger((current: boolean) => !current);
      successNotification(t('common.message.save1'));
      setTab(SemesterContents.Test);
    }
  }, [submitRes]);

  const onSubmit = () => {
    if (!semesterCode) return;
    if (!name) {
      return infoNotification(t('program.semester.message20'));
    }
    if (!eduStepCode) {
      return infoNotification(t('cbt.speakingRubric.1'));
    }
    if (!examDiv) {
      return infoNotification(t('program.semester.message19'));
    }
    if (!srtDate) {
      return infoNotification(t('program.semester.message21'));
    }
    if (!endDate) {
      return infoNotification(t('program.semester.message22'));
    }

    //특강학기인 경우 연도, 월 자동 선택
    let tempYearCode;
    let tempTestSeq;
    if (SEMESTER_TYPE.SPECIAL_CLASS !== selSemesterType) {
      tempYearCode = yearCode;
      tempTestSeq = testSeq;
    } else {
      tempYearCode = moment(srtDate).format('YYYY');
      tempTestSeq = moment(srtDate).format('M');
    }

    if (!tempYearCode) {
      return infoNotification(t('program.semester.message23'));
    }
    if (!tempTestSeq) {
      return infoNotification(t('program.semester.message24'));
    }

    const req = {
      eduStepCode,
      examDiv,
      name,
      srtDate,
      endDate,
      yearCode: tempYearCode,
      testSeq: tempTestSeq,
      checkYn,
      cacExamDate,
    };

    scheduleCode
      ? submitExecute(updateSemesterTest(scheduleCode, req))
      : submitExecute(createSemesterTest(Number(semesterCode), req));
  }

  const onCancel = () => {
    setExamDiv(undefined);
    setName('');
    setSrtDate('');
    setEndDate('');
    setYearCode(undefined);
    setTestSeq(undefined);
    setCacExamDate(undefined);
    setTab(SemesterContents.Test);
  }

  return (
    <>
      {isLoading || submitIsLoading ? <Loader /> : null}
      <ErrorResponse error={error} />
      <ErrorResponse error={submitError} />

      <div className='comm-table__area rowType edit'>
        <Descriptions
          bordered
          column={2}
          size="small"
          labelStyle={{ width: '15%' }}
          contentStyle={{ width: '35%' }}
        >
          <Descriptions.Item label={t('program.semester.column25')} span={2}>
            <input
              type="text"
              className="form-control"
              value={name}
              onChange={e => setName(e.target.value)}
            />
          </Descriptions.Item>
          <Descriptions.Item label={t('common.label.1')} span={2}>
            <SelectEduStep
              eduStepCode={eduStepCode}
              setEduStepCode={value => {
                setEduStepCode(value);
                setExamDiv(undefined);
              }}
              placeholder={t('student.student.placeholder6')}
              width={150}
              noDefault
            />
          </Descriptions.Item>
          <Descriptions.Item label={t('program.semester.column24')}>
            <div className="w-150p">
              <Select
                className="w-100"
                placeholder={t('common.button.select')}
                value={examDiv}
                onChange={value => setExamDiv(value)}
                disabled={!eduStepCode}
              >
                {
                  eduStepCode === 'EM'
                    ? <Select.Option value="MT">Monthly Test</Select.Option>
                    : <Select.Option value="PL">Prelim</Select.Option>
                }
                <Select.Option value="LT">Term Test</Select.Option>
              </Select>
            </div>
          </Descriptions.Item>
          <Descriptions.Item label={t('program.semester.column28')}>
            <Radio
              checked={checkYn === 'N'}
              onChange={() => setCheckYn('N')}
              text={t('program.semester.option3')}
            />
            <Radio
              className="ml-4"
              checked={checkYn === 'Y'}
              onChange={() => setCheckYn('Y')}
              text={t('program.semester.option4')}
            />
          </Descriptions.Item>
          <Descriptions.Item label={t('program.semester.column12')}>
            <div className="date-picker__area">
              <DatePicker
                value={srtDate ? moment(srtDate) : undefined}
                onChange={(e) => {
                  if (e === null) setSrtDate('');
                  else setSrtDate(moment(e).format('YYYYMMDD'));
                }}
              />
            </div>
          </Descriptions.Item>
          <Descriptions.Item label={t('program.semester.column13')}>
            <div className="date-picker__area">
              <DatePicker
                value={endDate ? moment(endDate) : undefined}
                onChange={(e) => {
                  if (e === null) setEndDate('');
                  else setEndDate(moment(e).format('YYYYMMDD'));
                }}
              />
            </div>
          </Descriptions.Item>
          <Descriptions.Item label={t('program.semester.column9')}>
            <div className="w-150p">
              <Select
                className="w-100"
                placeholder={t('common.button.select')}
                value={yearCode}
                onChange={value => setYearCode(value)}
                disabled={(SEMESTER_TYPE.SPECIAL_CLASS === selSemesterType)}
              >
                {getReverseYears(SY0005).map((item: any) => (
                  <Select.Option
                    key={item.detailCode}
                    value={item.detailCode}
                  >
                    {item.korName}
                  </Select.Option>
                ))}
              </Select>
            </div>
          </Descriptions.Item>
          <Descriptions.Item label={t('program.semester.column22')}>
            <div className="w-150p">
              <Select
                className="w-100"
                placeholder={t('common.button.select')}
                value={testSeq}
                onChange={value => setTestSeq(value)}
                disabled={(SEMESTER_TYPE.SPECIAL_CLASS === selSemesterType)}
              >
                {SY0011?.map((item: any) => (
                  <Select.Option
                    key={item.detailCode}
                    value={item.detailCode}
                  >
                    {item.korName}
                  </Select.Option>
                ))}
              </Select>
            </div>
          </Descriptions.Item>
          <Descriptions.Item label={t('CAC Date')} span={2}>
            <div className="date-picker__area">
              <DatePicker
                value={cacExamDate ? moment(cacExamDate) : undefined}
                onChange={(e) => {
                  if (e === null) setCacExamDate(undefined);
                  else setCacExamDate(moment(e).format('YYYYMMDD'));
                }}
              />
            </div>
          </Descriptions.Item>
        </Descriptions>
      </div>

      <div className="comm-bx__space border-bottom-light">
        <div className="comm-btn__area  right">
          <Button
            onClick={onSubmit}
            className="btn-basic pink"
            text={t('common.button.save')}
          />
          <Button
            onClick={onCancel}
            className="btn-basic gray"
            text={t('common.button.cancel')}
          />
        </div>
      </div>
    </>
  );
}

export default TestEdit;
